import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircleRounded, ExpandMoreRounded } from "@mui/icons-material";

import payload from "@app/payload";
import Text from "@components/Text";
import { t } from "@i18n";
import { isStripeProvider } from "@utils/provider";
import { buildSummaryPrice, getPlanIntervalLabel } from "@checkout/lib/summary";
import { getPlanSelectionConfig } from "@checkout/lib/planSelection";
import { getInitials, resolveMediaUrl } from "@checkout/lib/media";
import PlanCard from "@checkout/components/PlanCard";
import SummaryScrim from "@checkout/components/SummaryScrim";
import ThemeToggle from "@checkout/components/ThemeToggle";
import { useSummaryOverlaySpacing } from "@checkout/hooks/useSummaryOverlaySpacing";

import './styles.scss';

export default function CheckoutSummary({
  plan,
  valuesRef,
  provider,
  onPlanSelect,
}: {
  plan: any;
  valuesRef: any;
  provider: string;
  onPlanSelect: (plan: any) => void;
}) {
  const banner = resolveMediaUrl(
    payload.banner || payload.hero || payload.cover || payload.image,
  );
  const communityName = payload.name || "Ginastee";
  // still computed here so the overlay-spacing hook re-measures when the price line changes
  const summary = buildSummaryPrice(
    plan,
    valuesRef.current.installments,
    valuesRef.current.coupon,
  );
  const planSelection = getPlanSelectionConfig(plan);

  const [plansSheetOpen, setPlansSheetOpen] = useState(false);
  const planTriggerRef = useRef<HTMLButtonElement | null>(null);

  const closePlansSheet = () => {
    setPlansSheetOpen(false);
    planTriggerRef.current?.focus();
  };

  useEffect(() => {
    if (!plansSheetOpen) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closePlansSheet();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plansSheetOpen]);

  const {
    panelRef: summaryPanelRef,
    headerRef: summaryHeaderRef,
    footerRef: summaryFooterRef,
  } = useSummaryOverlaySpacing([plan?.id, summary.price, summary.total]);

  return (
    <aside
      ref={summaryPanelRef}
      className="checkout-summary-panel"
      aria-label={t("checkout.summary.ariaLabel")}
    >
      {banner && (
        <img
          className="checkout-summary-bg"
          src={banner}
          alt=""
          aria-hidden="true"
        />
      )}

      <div className="checkout-summary-overlay" />

      <div ref={summaryHeaderRef} className="checkout-summary-header">
        <SummaryScrim />

        <div className="checkout-summary-brand">
          <div className="checkout-summary-avatar">
            {banner ? (
              <img src={banner} alt="" />
            ) : (
              <Text.span>{getInitials(communityName)}</Text.span>
            )}
          </div>

          <div className="checkout-summary-brand-copy">
            <Text.subheading className="checkout-eyebrow">
              {t("checkout.summary.community")}
            </Text.subheading>
            <Text.h1>{communityName}</Text.h1>
          </div>

          <ThemeToggle />
        </div>

        <div className="checkout-plans-header">
          <div>
            <Text.subheading className="checkout-eyebrow">
              {t("checkout.summary.availablePlans")}
            </Text.subheading>
            <Text.h2>{t("checkout.summary.chooseAccess")}</Text.h2>
          </div>
        </div>
      </div>

      {/* mobile replaces the inline list with the selected plan opening a bottom sheet */}
      {plan && (
        <button
          ref={planTriggerRef}
          type="button"
          className="checkout-plan-option checkout-plan-trigger"
          aria-haspopup="dialog"
          aria-expanded={plansSheetOpen}
          disabled={planSelection.isInteractionDisabled}
          onClick={() => setPlansSheetOpen(true)}
        >
          <PlanOptionContent plan={plan} provider={provider} />

          <span className="checkout-plan-option-marker" aria-hidden="true">
            {!planSelection.isInteractionDisabled && <ExpandMoreRounded />}
          </span>
        </button>
      )}

      <div
        className="checkout-plans-list"
        role="radiogroup"
        aria-label={t("checkout.summary.availablePlans")}
      >
        {planSelection.visiblePlans.map((availablePlan: any) => (
          <PlanOption
            key={availablePlan.id}
            plan={availablePlan}
            provider={provider}
            selected={String(availablePlan.id) === String(plan?.id)}
            disabled={planSelection.isInteractionDisabled}
            onSelect={() => onPlanSelect?.(availablePlan)}
          />
        ))}
      </div>

      <div ref={summaryFooterRef} className="checkout-summary-footer">
        <SummaryScrim />

        <PlanCard plan={plan} valuesRef={valuesRef} />
      </div>

      {plansSheetOpen &&
        createPortal(
          <div className="checkout-plans-sheet-root">
            <div
              className="checkout-plans-sheet-backdrop"
              onClick={closePlansSheet}
            />

            <div
              className="checkout-plans-sheet"
              role="dialog"
              aria-modal="true"
              aria-label={t("checkout.summary.availablePlans")}
            >
              <span className="checkout-plans-sheet-handle" aria-hidden="true" />

              <div className="checkout-plans-sheet-header">
                <Text.subheading className="checkout-eyebrow">
                  {t("checkout.summary.availablePlans")}
                </Text.subheading>
                <Text.h2>{t("checkout.summary.chooseAccess")}</Text.h2>
              </div>

              <div
                className="checkout-plans-sheet-list"
                role="radiogroup"
                aria-label={t("checkout.summary.availablePlans")}
              >
                {planSelection.visiblePlans.map((availablePlan: any) => (
                  <PlanOption
                    key={availablePlan.id}
                    plan={availablePlan}
                    provider={provider}
                    selected={String(availablePlan.id) === String(plan?.id)}
                    disabled={planSelection.isInteractionDisabled}
                    onSelect={() => {
                      onPlanSelect?.(availablePlan);
                      closePlansSheet();
                    }}
                  />
                ))}
              </div>

              <div className="checkout-plans-sheet-fade" aria-hidden="true" />
            </div>
          </div>,
          document.body,
        )}
    </aside>
  );
}

function PlanOptionContent({ plan, provider }: any) {
  const optionPlan = {
    ...plan,
    currency: isStripeProvider(provider) ? "USD" : "BRL",
  };
  const summary = buildSummaryPrice(optionPlan, plan.installments || 1, null);
  const interval = getPlanIntervalLabel(plan);
  const banner = resolveMediaUrl(
    payload.banner || payload.hero || payload.cover || payload.image,
  );

  return (
    <>
      <span className="checkout-plan-option-banner">
        {banner ? (
          <img src={banner} alt="" />
        ) : (
          <Text.span>{getInitials(payload.name || "G")}</Text.span>
        )}
      </span>

      <span className="checkout-plan-option-content">
        <Text.span className="checkout-plan-option-title">
          {plan.label || t("common.plan")}
        </Text.span>
        <Text.span className="checkout-plan-option-price">
          {summary.showInstallments &&
            `${t("common.installmentPrefix", { count: summary.installments })} `}
          {summary.price}
          {!summary.isLifetime && <Text.small> / {interval}</Text.small>}
        </Text.span>
      </span>
    </>
  );
}

function PlanOption({ plan, provider, selected, disabled, onSelect }: any) {
  return (
    <button
      type="button"
      className={`checkout-plan-option ${selected ? "checkout-plan-option--selected" : ""}`}
      aria-checked={selected}
      role="radio"
      disabled={disabled}
      onClick={onSelect}
    >
      <PlanOptionContent plan={plan} provider={provider} />

      <span className="checkout-plan-option-marker">
        {selected && <CheckCircleRounded />}
      </span>
    </button>
  );
}
